import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { CartLine, Fit, Product } from '../types/product';
import { useScreenInit } from '../useScreenInit.js';
interface CartContextValue {
  lines: CartLine[];
  isOpen: boolean;
  count: number;
  subtotal: number;
  addLine: (product: Product, size: string, fit: Fit) => void;
  removeLine: (id: string, size: string, fit: Fit) => void;
  setQuantity: (id: string, size: string, fit: Fit, quantity: number) => void;
  openCart: () => void;
  closeCart: () => void;
}
const CartContext = createContext<CartContextValue | null>(null);
const matches = (line: CartLine, id: string, size: string, fit: Fit) => line.product.id === id && line.size === size && line.fit === fit;
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const screenInit = useScreenInit();
  const [lines, setLines] = useState<CartLine[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(Boolean(screenInit?.cartOpen));
  const addLine = useCallback((product: Product, size: string, fit: Fit) => {
    setLines(current => {
      const existing = current.find(l => matches(l, product.id, size, fit));
      if (existing) {
        return current.map(l => l === existing ? {
          ...l,
          quantity: l.quantity + 1
        } : l);
      }
      return [...current, {
        product,
        size,
        fit,
        quantity: 1
      }];
    });
    setIsOpen(true);
  }, []);
  const removeLine = useCallback((id: string, size: string, fit: Fit) => {
    setLines(current => current.filter(l => !matches(l, id, size, fit)));
  }, []);
  const setQuantity = useCallback((id: string, size: string, fit: Fit, quantity: number) => {
    setLines(current => quantity <= 0 ? current.filter(l => !matches(l, id, size, fit)) : current.map(l => matches(l, id, size, fit) ? {
      ...l,
      quantity
    } : l));
  }, []);
  const value = useMemo<CartContextValue>(() => {
    const count = lines.reduce((sum, l) => sum + l.quantity, 0);
    const subtotal = lines.reduce((sum, l) => sum + l.quantity * l.product.price, 0);
    return {
      lines,
      isOpen,
      count,
      subtotal,
      addLine,
      removeLine,
      setQuantity,
      openCart: () => setIsOpen(true),
      closeCart: () => setIsOpen(false)
    };
  }, [lines, isOpen, addLine, removeLine, setQuantity]);
  return <CartContext.Provider value={value} data-id="element-210" data-mp="3od5a31iqmscz">{children}</CartContext.Provider>;
}
export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
}